import { ReactNode } from 'react';
import { m } from 'framer-motion';
import { Container, Typography } from '@mui/material';
import usePermissions from '../hooks/usePermissions';
import { PermissionCode } from '../constants/permissions';
import { MotionContainer, varBounce } from '../components/animate';
import { ForbiddenIllustration } from '../assets';

// ----------------------------------------------------------------------

type PermissionGuardProps = {
  hasContent?: boolean;
  permissions?: PermissionCode[];
  /** When multiple permissions are listed, `any` = at least one; `all` = every code. */
  match?: 'any' | 'all';
  children: ReactNode;
};

function PermissionDeniedContent() {
  return (
    <Container component={MotionContainer} sx={{ textAlign: 'center' }}>
      <m.div variants={varBounce().in}>
        <Typography variant="h3" paragraph>
          Permission Denied
        </Typography>
      </m.div>

      <m.div variants={varBounce().in}>
        <Typography sx={{ color: 'text.secondary' }}>
          You do not have permission to access this page
        </Typography>
      </m.div>

      <m.div variants={varBounce().in}>
        <ForbiddenIllustration sx={{ height: 260, my: { xs: 5, sm: 10 } }} />
      </m.div>
    </Container>
  );
}

export default function PermissionGuard({
  hasContent,
  permissions,
  match = 'any',
  children,
}: PermissionGuardProps) {
  const { canAny, canAll } = usePermissions();

  if (typeof permissions === 'undefined') {
    return <>{children}</>;
  }

  const allowed = match === 'all' ? canAll(permissions) : canAny(permissions);

  if (!allowed) {
    return hasContent ? <PermissionDeniedContent /> : null;
  }

  return <>{children}</>;
}
